import React, { useState, useEffect, useCallback } from "react";

import { FaFolder, FaFileAlt, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { getJobNumbers } from "../../../services/api/core";

import Folder from "./Folder";
import File from "./File";
import { itemMatchesSearchQuery } from "../../../utils/common/searchFilter";

/**
 * Filters folders for a specific view type.
 *
 * @param {Array} folders - List of folder objects.
 * @param {string} structure - The folder structure type.
 * @returns {Array} - Filtered list of folder objects.
 */
const filterFolders = async (folders, structure) => {
  if (structure === "none") return [];
  if (structure === "projects") {
    const jobNumbers = await getJobNumbers();
    const jobNumbersList = jobNumbers.map((job) => job.number.split("-")[0]);
    const jobFolders =
      folders?.filter((folder) => /^\d{3,}/.test(folder.name.split(" ")[0])) ||
      [];
    return jobFolders.filter((folder) =>
      jobNumbersList.some((jobNumber) => folder.name.startsWith(jobNumber))
    );
  }
  if (structure === "all") {
    return folders?.filter((folder) => folder.name !== "Shared") || [];
  }
};

/**
 * Filters files based on specific criteria.
 *
 * @param {Array} files - List of file objects.
 * @param {string} structure - The file structure type.
 * @returns {Array} - Filtered list of file objects.
 */
const filterFiles = (files, structure) => {
  if (structure === "none") return [];
  if (structure === "documents") {
    const allowedExtensions = ["pdf", "docx", "xlsx"];
    return (
      files?.filter((file) =>
        allowedExtensions.includes(file.name.split(".").pop().toLowerCase())
      ) || []
    );
  }
};

/**
 * Drive folder/file list for one explorer pane. Filters items by `structure`
 * (projects / documents / all / none), applies search and sort, then renders
 * {@link Folder} / {@link File} rows or a grid.
 *
 * @param {Object} props Component props.
 * @param {string} props.title Header label (Name column).
 * @param {Object} props.folderData Current folder payload (`folders`, `files`).
 * @param {{folders: string, files: string}} props.structure Filter mode per item type.
 * @param {string} props.searchQuery Name search text.
 * @param {boolean} props.isGridView Grid vs list layout.
 * @param {Function} props.handleFolderClick Called with the folder path.
 * @param {Function} props.handleFileClick Called with the file path.
 * @param {boolean} [props.comments] When true, show the Comments column.
 * @returns {JSX.Element}
 */
const Explorer = ({
  title,
  folderData,
  structure,
  searchQuery,
  isGridView,
  handleFolderClick,
  handleFileClick,
  comments,
}) => {
  const [combinedItems, setCombinedItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });

  /**
   * Rebuild the combined folder+file list from `folderData` using `structure`.
   *
   * @returns {Promise<void>}
   */
  const updateCombinedItems = useCallback(async () => {
    if (folderData) {
      const filteredFolders =
        (await filterFolders(folderData.folders, structure["folders"])) || [];
      const filteredFiles =
        filterFiles(folderData.files, structure["files"]) || [];
      const items = [
        ...filteredFolders.map((folder, index) => ({
          ...folder,
          type: "folder",
          uniqueKey: `folder-${folder.id || index}`,
        })),
        ...filteredFiles.map((file, index) => ({
          ...file,
          type: "file",
          uniqueKey: `file-${file.id || index}`,
        })),
      ];
      setCombinedItems(items);
    }
  }, [folderData, structure]);

  useEffect(() => {
    updateCombinedItems();
  }, [folderData, updateCombinedItems]);

  const filteredItems = combinedItems.filter((item) =>
    itemMatchesSearchQuery(item, searchQuery, ["name"])
  );

  const sortedItems = [...filteredItems].sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    if (a[sortConfig.key] < b[sortConfig.key])
      return sortConfig.direction === "asc" ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key])
      return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  /**
   * Toggle sort key/direction. Same key flips asc/desc; a new key starts at desc
   * after the first click (because current key no longer matches).
   *
   * @param {string} key Item field to sort by (`name` or `comments`).
   * @returns {void}
   */
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  return (
    <div className="bg-white shadow-md rounded-lg m-auto mt-2">
      <div
        className={`grid ${comments ? "grid-cols-3" : "grid-cols-1"} gap-4 p-2 px-3 rounded-t-lg border-b font-semibold text-gray-600`}
      >
        <button
          onClick={() => handleSort("name")}
          className={`${comments ? "col-span-2" : "col-span-1"} text-left truncate flex items-center space-x-2`}
        >
          <span>{title}</span>
          {sortConfig.key === "name" && (
            sortConfig.direction === "asc" ? <FaArrowUp className="text-xs text-gray-400"/> : <FaArrowDown className="text-xs text-gray-400"/>
          )}
        </button>
        {comments && (
          <button
            onClick={() => handleSort("comments")}
            className="text-left truncate col-span-1 flex items-center space-x-2"
          >
            <span>Comments</span>
            {sortConfig.key === "comments" && (
              sortConfig.direction === "asc" ? <FaArrowUp  className="text-xs text-gray-400"/> : <FaArrowDown  className="text-xs text-gray-400"/>
            )}
          </button>
        )}
      </div>
      <div
        className={
          isGridView
            ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 p-2"
            : "p-2"
        }
      >
        {sortedItems.length === 0 ? (
          <div className="text-left text-gray-500 pl-2 ">
            <i>Empty</i>
          </div>
        ) : (
          sortedItems.map((item) =>
            item.type === "folder" ? (
              <Folder
                key={item.uniqueKey}
                name={item.name}
                icon={<FaFolder className="text-yellow-500 min-w-6" />}
                onClick={() => handleFolderClick(item.path)}
                isGridView={isGridView}
                comments={comments}
              />
            ) : (
              <File
                key={item.uniqueKey}
                name={item.name}
                icon={<FaFileAlt className="text-red-500 min-w-6" />}
                onClick={() => handleFileClick(item.path)}
                isGridView={isGridView}
                comments={comments ? "Placeholder comment" : null}
                entryId={item.entry_id}
              />
            )
          )
        )}
      </div>
    </div>
  );
};

export default Explorer;
